import { useQuery } from '@apollo/client/react'
import { ErrorState, LoadingState, useLanguage, useTranslate } from '@athena/ui'
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'

import { useSignInSearch } from './sign-in-search.js'
import { MeDocument, MyActiveTimedAttemptDocument } from '../graphql/generated/operations.js'

/**
 * The student half of the app.
 *
 * `data-density="student"` is the whole of the difference in chrome: same
 * tokens, same components, roomier rhythm and larger base text (handoff,
 * section 6). No component is forked for it.
 *
 * There is no shell here on purpose. A practice in progress holds the question
 * and nothing else, so the shell sits one level down on StudentShell, which
 * the runner's route deliberately sits outside of.
 */
export function StudentLayout() {
  const t = useTranslate()
  const language = useLanguage()
  const navigate = useNavigate()
  const { data, loading, error } = useQuery(MeDocument)
  const signInSearch = useSignInSearch()
  const viewer = data?.me

  /**
   * The timed practice this student left running, if any.
   *
   * Asked of the server, once, on the way into the student area, and skipped
   * entirely until we know there is a student. Nothing about this comes from
   * `localStorage`: storage survives a logout and a different child on the
   * same laptop, and it is editable by the person it is about — so it can send
   * somebody into a practice that is not theirs and is not running.
   *
   * `network-only` because the whole question is "what is true now". A cached
   * answer from before the practice was submitted is exactly the wrong one.
   */
  const { data: active } = useQuery(MyActiveTimedAttemptDocument, {
    skip: loading || !!error || !viewer?.student || !viewer.onboarding.complete,
    fetchPolicy: 'network-only',
  })
  const activeAttemptId = active?.myActiveTimedAttempt?.id ?? null

  const pathname = useRouterState({ select: (state) => state.location.pathname })
  /**
   * Which attempt this session has already been sent back to.
   *
   * The redirect happens once per attempt per page load, not on every
   * navigation, and that is the difference between recovering a practice and
   * trapping a student in one. Somebody who lands here after a crash is put
   * back in their exam; somebody who then deliberately opens their progress
   * screen is left alone.
   */
  const recovered = useRef<string | null>(null)

  useEffect(() => {
    if (!activeAttemptId || recovered.current === activeAttemptId) return
    // Already inside it — a refresh, or the tab that started it. Nothing to do,
    // and navigating would be a pointless remount of the runner.
    if (pathname.includes(`/learn/attempt/${activeAttemptId}`)) {
      recovered.current = activeAttemptId
      return
    }

    recovered.current = activeAttemptId
    void navigate({
      to: '/$language/learn/attempt/$attemptId',
      params: { language, attemptId: activeAttemptId },
      replace: true,
    })
  }, [activeAttemptId, pathname, navigate, language])

  useEffect(() => {
    if (loading || error) return
    if (!viewer) {
      void navigate({
        to: '/$language/sign-in',
        params: { language },
        // Back here once signed in, rather than to the dashboard.
        search: signInSearch(),
        replace: true,
      })
      return
    }
    if (!viewer.onboarding.complete) {
      void navigate({ to: '/$language/onboarding', params: { language }, replace: true })
      return
    }
    if (!viewer.student) {
      // A tutor who wandered in goes to their own side; anyone else has not
      // accepted an invitation yet.
      void navigate({
        to: viewer.tutor ? '/$language' : '/$language/onboarding',
        params: { language },
        replace: true,
      })
    }
  }, [loading, error, viewer, navigate, language, signInSearch])

  if (loading) {
    return <LoadingState>{t('common.loading')}</LoadingState>
  }

  if (error) {
    return <ErrorState>{t('appError.studentUnreachable')}</ErrorState>
  }

  if (!viewer?.student || !viewer.onboarding.complete) return null

  return (
    <div data-density="student" className="min-h-dvh bg-background">
      <Outlet />
    </div>
  )
}
